/**
 * HAMS SSO: hash access token + postMessage backup from User Service portal.
 */
export const ERP_SSO_READY = "ZITR_ERP_SSO_READY";
export const ERP_SSO_PAYLOAD = "ZITR_ERP_SSO_PAYLOAD";

export const PENDING_ERP_SSO_KEY = "pending_erp_sso";
const PENDING_HASH_ACCESS_KEY = "pending_hash_access";

export function portalOriginGuess(): string {
  const configured = (import.meta.env.VITE_PORTAL_URL as string | undefined)?.trim()
    || "http://localhost:3000";
  try {
    const base = new URL(configured);
    if (base.hostname !== "localhost" && base.hostname !== "127.0.0.1") {
      return base.origin;
    }
    const host = window.location.hostname;
    const port = base.port || (base.protocol === "https:" ? "" : "3000");
    return `${base.protocol}//${host}${port ? `:${port}` : ""}`;
  } catch {
    return "http://localhost:3000";
  }
}

export function clearSsoQuery() {
  const url = new URL(window.location.href);
  ["erp_sso", "sso_code", "access", "refresh", "access_token", "refresh_token", "tenant_host", "_ts"].forEach((k) => {
    url.searchParams.delete(k);
  });
  url.hash = "";
  window.history.replaceState({}, "", url.pathname + url.search);
}

function takeHashParams(): URLSearchParams {
  const raw = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams();
  if (!raw) return params;
  for (const part of raw.split("&")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    const key = decodeURIComponent(part.slice(0, i));
    const value = decodeURIComponent(part.slice(i + 1));
    params.set(key, value);
  }
  return params;
}

function tenantSlugFromHost(hostRaw: string): string {
  const host = (hostRaw || "").split(":")[0].trim().toLowerCase();
  if (!host) return "";
  const first = host.split(".")[0];
  if (!first || first === "www" || first === "localhost" || first === "erp" || first === "public") return "";
  return first;
}

function schemaFromJwt(access?: string): string {
  if (!access) return "";
  try {
    const part = access.split(".")[1];
    if (!part) return "";
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(padded)) as { schema_name?: string };
    const schema = String(json.schema_name || "").trim().toLowerCase();
    if (!schema || schema === "public") return "";
    return schema;
  } catch {
    return "";
  }
}

function hamsFrontendSuffix(): string {
  return String(import.meta.env.VITE_FRONTEND_DOMAIN_SUFFIX || "")
    .replace(/^\./, "")
    .toLowerCase();
}

function redirectApexSsoToTenant(): boolean {
  const suffix = hamsFrontendSuffix();
  const host = window.location.hostname.toLowerCase();
  if (!suffix || host !== suffix) return false;

  const live = readLiveHashAccess();
  const params = new URLSearchParams(window.location.search);
  const schema = schemaFromJwt(live?.access)
    || tenantSlugFromHost(live?.tenantHost || params.get("tenant_host") || "");
  if (!schema) return false;

  const bounceKey = "hams_apex_bounce";
  try {
    if (sessionStorage.getItem(bounceKey) === schema) return false;
    sessionStorage.setItem(bounceKey, schema);
  } catch {
    /* ignore */
  }

  const next = new URL(window.location.href);
  next.hostname = `${schema}.${suffix}`;
  window.location.replace(next.toString());
  return true;
}

function readLiveHashAccess(): { access: string; refresh?: string; tenantHost?: string } | null {
  const params = new URLSearchParams(window.location.search);
  const hash = takeHashParams();
  const access = hash.get("access") || params.get("access") || params.get("access_token");
  if (!access) return null;
  const refresh = hash.get("refresh") || params.get("refresh") || params.get("refresh_token") || undefined;
  const tenantHost = hash.get("tenant_host") || params.get("tenant_host") || undefined;
  return { access, refresh, tenantHost };
}

function stashHashAccess(parsed: { access: string; refresh?: string; tenantHost?: string }) {
  try {
    sessionStorage.setItem(PENDING_HASH_ACCESS_KEY, JSON.stringify(parsed));
  } catch {
    /* ignore */
  }
  if (parsed.tenantHost) {
    try {
      localStorage.setItem("erp_tenant_host", parsed.tenantHost);
    } catch {
      /* ignore */
    }
  }
  markErpSsoPending();
  clearSsoQuery();
}

if (typeof window !== "undefined") {
  if (!redirectApexSsoToTenant()) {
    const live = readLiveHashAccess();
    if (live?.access) {
      stashHashAccess(live);
    } else {
      const params = new URLSearchParams(window.location.search);
      if (params.get("erp_sso") === "1") {
        const tenantHost = params.get("tenant_host");
        if (tenantHost) {
          try {
            localStorage.setItem("erp_tenant_host", tenantHost);
          } catch {
            /* ignore */
          }
        }
        markErpSsoPending();
        clearSsoQuery();
      }
    }
  }
}

export function takeHashAccess(): { access: string; refresh?: string; tenantHost?: string } | null {
  const live = readLiveHashAccess();
  if (live?.access) {
    stashHashAccess(live);
    return live;
  }
  try {
    const raw = sessionStorage.getItem(PENDING_HASH_ACCESS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { access?: string; refresh?: string; tenantHost?: string };
    if (parsed?.access) return parsed as { access: string; refresh?: string; tenantHost?: string };
  } catch {
    /* ignore */
  }
  return null;
}

export function isErpSsoLaunch(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get("erp_sso") === "1") return true;
  if (takeHashAccess()) return true;
  try {
    return sessionStorage.getItem(PENDING_ERP_SSO_KEY) === "1";
  } catch {
    return false;
  }
}

export function markErpSsoPending(): void {
  try {
    sessionStorage.setItem(PENDING_ERP_SSO_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function clearErpSsoPending(): void {
  try {
    sessionStorage.removeItem(PENDING_ERP_SSO_KEY);
    sessionStorage.removeItem(PENDING_HASH_ACCESS_KEY);
  } catch {
    /* ignore */
  }
}

export function isIntegratedMode(): boolean {
  return import.meta.env.VITE_ERP_INTEGRATED !== "false";
}

function isLoopbackHost(host: string): boolean {
  const h = (host || "").split(":")[0].toLowerCase();
  return h === "localhost" || h === "127.0.0.1";
}

function isLocalDevHost(host: string): boolean {
  const h = (host || "").split(":")[0].toLowerCase();
  return isLoopbackHost(h) || h.endsWith(".localhost");
}

function hostLooksLikePortalTenant(host: string, portalApex: string): boolean {
  const h = (host || "").split(":")[0].toLowerCase();
  const apex = (portalApex || "").split(":")[0].toLowerCase();
  if (!h) return false;
  if (isLocalDevHost(h) && isLocalDevHost(apex) && h.includes(".")) return true;
  if (!apex || isLoopbackHost(apex)) return false;
  return h === apex || h.endsWith(`.${apex}`);
}

export function configuredPortalUrl(): string {
  return (import.meta.env.VITE_PORTAL_URL as string | undefined)?.trim() || "http://localhost:3000";
}

export function tenantPortalLoginUrl(tenantHostOverride?: string | null): string {
  const configured = configuredPortalUrl();
  let base: URL;
  try {
    base = new URL(configured);
  } catch {
    return "http://localhost:3000/login";
  }

  const portalApex = base.hostname.toLowerCase();
  const stored = (tenantHostOverride
    || (typeof localStorage !== "undefined" ? localStorage.getItem("erp_tenant_host") : "")
    || "").replace(/^https?:\/\//i, "").split("/")[0].trim().toLowerCase();
  const storedHost = stored.split(":")[0];
  const pageHost = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();

  let portalHost = "";
  if (storedHost && hostLooksLikePortalTenant(storedHost, portalApex)) {
    portalHost = storedHost;
  } else {
    const slug = tenantSlugFromHost(storedHost) || tenantSlugFromHost(pageHost);
    if (slug) {
      portalHost = isLocalDevHost(portalApex) ? `${slug}.localhost` : `${slug}.${portalApex}`;
    }
  }

  if (!portalHost) {
    return `${base.origin}/login`;
  }
  if (isLocalDevHost(portalHost)) {
    const port = base.port || (base.protocol === "https:" ? "" : "3000");
    return `${base.protocol}//${portalHost}${port ? `:${port}` : ""}/login`;
  }
  return `${base.protocol}//${portalHost}/login`;
}

export function redirectToPortalLogin(tenantHostOverride?: string | null): void {
  const host = tenantHostOverride || localStorage.getItem("erp_tenant_host");
  window.location.replace(tenantPortalLoginUrl(host));
}
